// Alerts — the raised-flag worklist.
//
// Deliberately a WORKLIST, not a dashboard: rows a human picks up and resolves.
// No trend line, no rate, no benchmark, no "flags this week". Those are metrics,
// and metrics in this console originate in curated BigQuery/Dataform, never in a
// Postgres aggregate computed here or a count summed in the browser. The one
// number shown is how many rows are currently open, which is the size of the
// list you are looking at.
//
// Gated on its own `alerts` area (migration 0102): triaging flags and reading
// every client conversation are different jobs, so on-call can hold one without
// the other.
import {
  Badge,
  Button,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  facetOptions,
  Input,
  isDateRangeActive,
  SortableTh,
  TableExport,
  TableFilters,
  type DateRange,
  type FilterColumn,
  useColumnFilters,
  useRestoredState,
  useTableSort,
  useToast,
} from "@recruiter/shared";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { AreaBoundary } from "../components/area-gate";
import { ArrowRightIcon } from "../components/icons";
import { ALERTS_TABS, SectionTabs } from "../components/section-tabs";
import { EmptyState, TableSkeleton } from "../components/states";
import { ApiError, type ExplorerFlag, resolveAlert } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { adminKeys, alertsQuery } from "../lib/queries";
import { ExplorerProjectLink } from "../components/explorer-link";
import { useDebounced } from "../lib/use-debounced";
import { useWriteAccess } from "../lib/use-access";
import { useCountryFilterOptions } from "../components/country-filter";
import { UNKNOWN_COUNTRY } from "../lib/country";
import { DEVICE_FILTER_OPTIONS } from "../lib/device";
import "./alerts.css";

/** Reasons carry different urgency; the tone makes the list scannable.
 *  An unknown reason falls back to neutral rather than crashing — the backend
 *  vocabulary can grow ahead of this map. */
function reasonTone(reason: string): "danger" | "warning" | "neutral" {
  if (reason === "agent_error" || reason === "chat_misuse") return "danger";
  if (reason === "human_requested" || reason === "client_waiting" || reason === "frustration") {
    return "warning";
  }
  // `seller_intent` reaches no list this map serves: it is a TAG-ONLY flag reason
  // (`recruiter/repo/attention.TAG_ONLY_REASONS`) that the backend keeps out of
  // every alert read, and it renders as the row's "Talent, not a client" tag
  // instead. Mira redirects those people in the same turn, so an alert row was
  // never something an operator could act on.
  return "neutral";
}

const ALERT_SORT = {
  reason: (f: ExplorerFlag) => f.reason,
  detail: (f: ExplorerFlag) => f.detail,
  project: (f: ExplorerFlag) => f.projectName,
  client: (f: ExplorerFlag) => f.ownerDisplayName ?? f.ownerEmail,
  raised: (f: ExplorerFlag) => f.createdAt,
} as const;

/** Where the ones we know about sit in the reason filter: most urgent first,
 *  matching the tones the rows are badged with. A reason outside this list
 *  simply follows, alphabetically. */
const ALERT_REASON_ORDER = [
  "agent_error",
  "chat_misuse",
  "human_requested",
  "client_waiting",
  "frustration",
] as const;

const reasonLabel = (value: string): string => value.replaceAll("_", " ");

export function AlertsPage() {
  // Restored on a history BACK hop (`@recruiter/shared`'s `view-state.ts`). It
  // matters more here than on an in-memory table: this selection KEYS the query,
  // so losing it on the way back from a project did not just clear the controls,
  // it refetched the whole unnarrowed worklist and dropped the rep back at the top
  // of it. The pages themselves come back with the Query cache under the same key.
  const [filter, setFilter] = useRestoredState("alerts/search", "");
  const [reasons, setReasons] = useRestoredState<readonly string[]>("alerts/reasons", []);
  // The flagged project's creation stamps (device, origin country), the
  // console-wide filters, narrowing SERVER-side like the reason.
  const [devices, setDevices] = useRestoredState<readonly string[]>("alerts/devices", []);
  const [countries, setCountries] = useRestoredState<readonly string[]>("alerts/countries", []);
  const countryOptions = useCountryFilterOptions();
  // The cohort range. Local state like the filters beside it, and it narrows
  // SERVER-side for the same reason they do: this list is keyset-paged, so a
  // browser-side date filter could only ever cover the pages already fetched.
  const [range, setRange] = useRestoredState<DateRange>("alerts/range", EMPTY_DATE_RANGE);
  // Debounced so a keystroke does not become a request: the text box narrows
  // SERVER-side now, like the reason filter.
  const q = useDebounced(filter.trim(), 300);
  const queryClient = useQueryClient();
  const toast = useToast();
  const alerts = useInfiniteQuery(alertsQuery({ q, reasons, devices, countries, range }));
  // Resolving is a claim that a HUMAN handled it, so it is the `full` half of the
  // Alerts area. A read-only holder watches the same queue and marks nothing
  // done — the route refuses them, so the column would be a row of buttons that
  // always 403.
  // `known` keeps the read-only SENTENCE off the screen until we can justify it:
  // the column is hidden while the grant query is pending (right for a control),
  // but claiming someone's access is read-only before we know is a lie a full
  // holder reads on every cold load.
  const { canWrite: canResolve, known: accessKnown } = useWriteAccess("alerts");

  const resolve = useMutation({
    mutationFn: (flagId: string) => resolveAlert(flagId),
    onSuccess: () => {
      toast.add({ title: "Alert resolved", data: { tone: "success" } });
      void queryClient.invalidateQueries({ queryKey: adminKeys.alerts });
    },
    onError: (error: unknown) => {
      // A 404 means someone else resolved it first — a race between two reps on
      // the same worklist, not a failure. Say so plainly and refresh, so the row
      // disappears instead of leaving a button that will never work.
      if (error instanceof ApiError && error.status === 404) {
        toast.add({ title: "Already resolved", data: { tone: "neutral" } });
        void queryClient.invalidateQueries({ queryKey: adminKeys.alerts });
        return;
      }
      toast.add({
        title: "Couldn't resolve that alert",
        description: error instanceof ApiError ? error.detail : undefined,
        data: { tone: "danger" },
      });
    },
  });

  // Filters and the text box narrow SERVER-side, across the WHOLE worklist:
  // the feed is keyset-paged, so a browser-side filter could only ever cover
  // the pages already fetched, and it used to do exactly that. The selection
  // keys the query (a new selection is a new list with its own cursor), and
  // the rows arriving here already match it.
  const all: ExplorerFlag[] = useMemo(
    () => alerts.data?.pages.flatMap((p) => p.flags) ?? [],
    [alerts.data],
  );
  // The reason options come from the response's store-wide facet, so the
  // control offers the full vocabulary even while a selection or the search
  // has narrowed the rows to one kind (deriving them from the filtered rows
  // would collapse the popover to whatever is ticked). A reason the backend
  // starts raising tomorrow still appears without a release.
  const knownReasons = alerts.data?.pages[0]?.reasons;
  const columns = useMemo<readonly FilterColumn<ExplorerFlag>[]>(
    () => [
      {
        key: "reason",
        label: "Filter by reason",
        allLabel: "All reasons",
        value: (f) => f.reason,
        optionLabel: reasonLabel,
        options: facetOptions(knownReasons ?? [], reasonLabel, ALERT_REASON_ORDER),
        selected: reasons,
        onChange: setReasons,
      },
      {
        key: "device",
        label: "Filter by device",
        allLabel: "All devices",
        // Null IS "unknown" server-side (no stamp recorded), so the local pass
        // over the loaded rows agrees with the SQL narrowing.
        value: (f) => f.createdDevice ?? "unknown",
        options: DEVICE_FILTER_OPTIONS,
        selected: devices,
        onChange: setDevices,
      },
      {
        key: "country",
        label: "Filter by country",
        allLabel: "All countries",
        value: (f) => f.originCountry ?? UNKNOWN_COUNTRY,
        options: countryOptions,
        selected: countries,
        onChange: setCountries,
      },
    ],
    // The setters are stable, listed because they reach this file through a
    // wrapper hook where eslint cannot see that.
    [
      knownReasons,
      reasons,
      devices,
      countries,
      countryOptions,
      setReasons,
      setDevices,
      setCountries,
    ],
  );
  // Every column here is CONTROLLED (the selection keys the server query above),
  // so the hook owns no selection of its own and there is nothing for it to
  // restore. The sort is the browser-side half, and does restore.
  const filters = useColumnFilters(all, columns);
  const rows = filters.rows;
  const sorted = useTableSort(rows, ALERT_SORT, null, { restore: "alerts/sort" });
  const narrowed = filter.trim() !== "" || filters.active || isDateRangeActive(range);

  if (alerts.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary area="alerts" error={alerts.error} onRetry={() => void alerts.refetch()} />
      </div>
    );
  }

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Alerts</h1>
        <p className="ad-page__sub">
          {accessKnown && !canResolve
            ? "Conversations Mira flagged for a human. Your access to Alerts is read only, so nothing here can be resolved."
            : "Conversations Mira flagged for a human. Resolve one to clear it."}
        </p>
      </header>

      <SectionTabs label="Alerts sections" tabs={ALERTS_TABS} />

      <div className="al-toolbar">
        <Input
          type="search"
          inputSize="sm"
          placeholder="Filter by reason, project, or client…"
          aria-label="Filter alerts"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <DateRangeFilter label="Raised" value={range} onChange={setRange} />
        <TableFilters
          controls={filters.controls}
          active={narrowed}
          onClear={() => {
            setFilter("");
            filters.clear();
            setRange(EMPTY_DATE_RANGE);
          }}
        />
        {/* Says "loaded", not "open". This list is keyset-paged, so the count is
            what has been fetched so far, and saying otherwise would turn a page
            boundary into a claim about how much work is waiting. The filters
            themselves cover the whole worklist: they run server-side, so every
            loaded row already matches them. */}
        {alerts.data ? (
          <span className="rc-toolbar__count">
            {rows.length.toLocaleString()} of {all.length.toLocaleString()} loaded
            {alerts.hasNextPage ? ", more available" : ""}
          </span>
        ) : null}
      </div>

      {alerts.isPending ? (
        <TableSkeleton columns={canResolve ? 6 : 5} />
      ) : all.length === 0 && !narrowed ? (
        <EmptyState
          title="Nothing needs a human"
          message="Flags raised by Mira will appear here."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={filter ? `No alerts match “${filter}”` : "No alerts match those filters"}
          message="The whole worklist was searched. Try clearing a filter."
        />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <TableExport name="alerts" label="alerts" />
              <thead>
                <tr>
                  <SortableTh sortKey="reason" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Reason
                  </SortableTh>
                  <SortableTh sortKey="detail" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    What happened
                  </SortableTh>
                  <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Project
                  </SortableTh>
                  <SortableTh sortKey="client" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Client
                  </SortableTh>
                  <SortableTh sortKey="raised" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Raised
                  </SortableTh>
                  {canResolve ? <th className="rc-actions-col">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((flag) => (
                  <tr key={flag.id}>
                    <td>
                      <Badge tone={reasonTone(flag.reason)} size="sm">
                        {flag.reason.replaceAll("_", " ")}
                      </Badge>
                    </td>
                    <td>{flag.detail || <span className="rc-cell-sub">No detail</span>}</td>
                    <td>
                      <ExplorerProjectLink
                        projectId={flag.projectId}
                        className="al-project-link"
                        denied={<span>{flag.projectName || "Untitled"}</span>}
                      >
                        {flag.projectName || "Untitled"}
                        <ArrowRightIcon size={14} />
                      </ExplorerProjectLink>
                    </td>
                    <td>
                      {flag.ownerDisplayName || flag.ownerEmail || (
                        <span className="rc-cell-sub">Guest</span>
                      )}
                      {flag.ownerDisplayName && flag.ownerEmail ? (
                        <span className="rc-cell-sub">{flag.ownerEmail}</span>
                      ) : null}
                    </td>
                    <td>{formatDateTime(flag.createdAt)}</td>
                    {canResolve ? (
                      <td>
                        <Button
                          variant="outline"
                          tone="neutral"
                          size="sm"
                          disabled={resolve.isPending && resolve.variables === flag.id}
                          onClick={() => resolve.mutate(flag.id)}
                        >
                          Resolve
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {alerts.hasNextPage ? (
            <div className="ex-load-more">
              <Button
                type="button"
                variant="outline"
                tone="neutral"
                size="sm"
                disabled={alerts.isFetchingNextPage}
                onClick={() => void alerts.fetchNextPage()}
              >
                {alerts.isFetchingNextPage ? "Loading…" : "Load more alerts"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
